import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { deleteProject, fetchProjectsWithPhase } from '@/lib/api';
import { ProjectPhaseBadge } from '@/components/ProjectPhaseBadge';
import { derivePhase, phaseBucket } from '@/lib/projectPhase';
import type { ProjectWithPhase } from '@/lib/api/projects';

type Row = ProjectWithPhase;
type View = 'alles' | 'projecten' | 'orders';

export function ProjectsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [view, setView] = useState<View>('alles');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const visibleRows = rows.filter((r) => {
    if (view === 'alles') return true;
    return phaseBucket(derivePhase(r.phaseInput)) === view;
  });

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      setRows(await fetchProjectsWithPhase());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string) {
    setDeleting(true);
    setError(null);
    try {
      await deleteProject(id);
      setRows((r) => r.filter((row) => row.id !== id));
      setConfirmId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verwijderen mislukt');
    } finally {
      setDeleting(false);
    }
  }

  const confirmRow = confirmId ? rows.find((r) => r.id === confirmId) : null;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-bmade-950 dark:text-white">Projecten</h1>
          <p className="text-sm text-bmade-600 dark:text-bmade-400">
            Pakket-projecten voor klanten. Klik op een project om pakketten te bouwen.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/projecten/nieuw')}
          className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
        >
          <Plus className="h-4 w-4" />
          Nieuw project
        </button>
      </header>

      <div className="flex items-center gap-1">
        {(
          [
            ['alles', 'Alles'],
            ['projecten', 'Projecten'],
            ['orders', 'Orders'],
          ] as [View, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              view === key
                ? 'bg-accent-500 text-white'
                : 'border border-bmade-200 text-bmade-700 hover:border-accent-400 dark:border-bmade-700 dark:text-bmade-300'
            }`}
          >
            {label}
            {key !== 'alles' && (
              <span className="ml-1 opacity-70">
                {rows.filter((r) => phaseBucket(derivePhase(r.phaseInput)) === key).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-bmade-200 bg-white dark:border-bmade-800 dark:bg-bmade-900/40">
        <table className="w-full text-sm">
          <thead className="bg-bmade-100/60 text-left text-xs uppercase tracking-wider text-bmade-600 dark:bg-bmade-900/60 dark:text-bmade-400">
            <tr>
              <th className="px-4 py-3 font-medium">Naam</th>
              <th className="px-4 py-3 font-medium">Klant</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Aantal pakketten</th>
              <th className="px-4 py-3 font-medium">Aangemaakt</th>
              <th className="w-12 px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-bmade-200 dark:divide-bmade-800">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-bmade-500">
                  Laden...
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-bmade-500">
                  {view === 'orders'
                    ? 'Nog geen orders. Maak een configuratie definitief om een order te starten.'
                    : view === 'projecten'
                      ? 'Geen projecten in concept of offerte.'
                      : 'Nog geen pakket-projecten. Begin met "Nieuw project".'}
                </td>
              </tr>
            ) : (
              visibleRows.map((row) => (
                <tr
                  key={row.id}
                  className="group transition-colors hover:bg-bmade-50 dark:hover:bg-bmade-900/60"
                >
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/projecten/${row.id}`}
                      className="text-accent-600 hover:underline dark:text-accent-400"
                    >
                      {row.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-bmade-700 dark:text-bmade-300">
                    {row.client?.company_name ?? <span className="text-bmade-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <ProjectPhaseBadge input={row.phaseInput} />
                  </td>
                  <td className="px-4 py-3 text-bmade-700 dark:text-bmade-300">
                    {row.package_count_planned ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-bmade-500 dark:text-bmade-400">
                    {new Date(row.created_at).toLocaleDateString('nl-NL')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setConfirmId(row.id)}
                      title="Verwijder project"
                      className="rounded-md p-1.5 text-bmade-400 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {confirmRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !deleting && setConfirmId(null)}
        >
          <div
            className="w-full max-w-md rounded-xl border border-bmade-200 bg-white p-6 shadow-xl dark:border-bmade-800 dark:bg-bmade-900"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-bmade-950 dark:text-white">
              Project verwijderen?
            </h2>
            <p className="mt-2 text-sm text-bmade-600 dark:text-bmade-400">
              <span className="font-medium text-bmade-950 dark:text-white">{confirmRow.name}</span>{' '}
              en alle pakketten, items en bestellingen worden permanent verwijderd. Niet terug te
              draaien.
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={deleting}
                onClick={() => setConfirmId(null)}
                className="rounded-md border border-bmade-200 px-3 py-1.5 text-sm text-bmade-700 hover:bg-bmade-100 disabled:opacity-40 dark:border-bmade-700 dark:text-bmade-300 dark:hover:bg-bmade-800"
              >
                Annuleer
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => void handleDelete(confirmRow.id)}
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {deleting ? 'Verwijderen...' : 'Definitief verwijderen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
